import { ResolutionType } from '@/app/entities/Material'
import { Input } from '@/view/components/ui/input'
import { Plus, Trash } from 'lucide-react'
import { forwardRef, useEffect, useState } from 'react'
import { ThumnailDropzone } from '../../ClassDetails/components/ThumbnailDropzone'
import { getVideoThumbnail } from '@/app/utils/getVideoThumbnail'
import { withCdn } from '@/app/utils/withCdn'

interface FileUploadProps {
  value: File[]
  onChange: (files: File[]) => void
  type: ResolutionType
  isDeletingFile: boolean
  onDelete?: (file: File) => void
}

export const FileUpload = forwardRef<HTMLInputElement, FileUploadProps>(
  ({ value, onChange, type, onDelete, isDeletingFile }, ref) => {
    const [thumbnails, setThumbnails] = useState<{ [key: string]: string }>({})

    useEffect(() => {
      const generateThumbnails = async () => {
        const newThumbnails: { [key: string]: string } = {}

        for (const file of value) {
          if (file.type.includes('video/') || file.type.includes('mp4'))
            newThumbnails[file.name] = await getVideoThumbnail(file)
          else if (file.type.includes('image/'))
            newThumbnails[file.name] = URL.createObjectURL(file)
          else newThumbnails[file.name] = withCdn(file.name)
        }

        setThumbnails(newThumbnails)
      }

      generateThumbnails()
    }, [value])

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files
      if (!files) return
      onChange([...value, ...Array.from(files)])
    }

    const handleDelete = (fileOrUrl: File) => {
      const identifier = fileOrUrl.name
      onChange(value.filter((item) => item.name !== identifier))
      onDelete?.(fileOrUrl)
      setThumbnails((prev) => {
        const newThumbnails = { ...prev }
        delete newThumbnails[fileOrUrl.name]
        return newThumbnails
      })
    }

    if (type === ResolutionType.Youtube) {
      return (
        <ThumnailDropzone
          onChange={(file) => onChange([file])}
          value={value?.[0]}
          className="w-full"
        />
      )
    }

    return (
      <div className="flex gap-2">
        <Input
          id={`file-upload-${type}`}
          type="file"
          multiple
          onChange={handleFileChange}
          accept="image/*,video/*"
          className="sr-only"
          ref={ref}
        />
        {value?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {value.map((file, index) => (
              <div
                key={index}
                className="group relative flex items-center justify-center"
              >
                <img
                  src={thumbnails[file.name]}
                  alt="Imagem"
                  className="h-16 w-16 rounded-md object-cover group-hover:brightness-[30%]"
                />
                <button
                  disabled={isDeletingFile}
                  type="button"
                  onClick={() => handleDelete(file)}
                  className="absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 transform group-hover:block"
                >
                  <Trash size={24} className="text-white" />
                </button>
              </div>
            ))}
          </div>
        )}
        <label
          htmlFor={`file-upload-${type}`}
          className="flex h-16 w-16 border-spacing-3 cursor-pointer items-center justify-center gap-2 rounded-md border border-solid border-gray-300 p-2 hover:border-gray-400 hover:bg-gray-100 active:border-gray-500 active:bg-gray-200"
        >
          <Plus size={24} className="text-primary" />
        </label>
      </div>
    )
  },
)

FileUpload.displayName = 'FileUpload'
